import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlus, FiUser, FiTrash2, FiEdit3, FiX, FiCheck, FiSave, FiDollarSign, FiAlertCircle, FiShield, FiTarget, FiInfo, FiClock } = FiIcons;

const ROX_CLASSES = ['Knight', 'Crusader', 'Wizard', 'Sage', 'Hunter', 'Bard', 'Dancer', 'Assassin', 'Rogue', 'Priest', 'Monk', 'Blacksmith', 'Alchemist'];

const PilotManagement = () => {
  const { user } = useAuth();
  const [pilots, setPilots] = useState([]);
  const [editingPilot, setEditingPilot] = useState(null);
  const [pilotCharacters, setPilotCharacters] = useState([]);
  const [newPilot, setNewPilot] = useState({ username: '', nickname: '' });
  const [newChar, setNewChar] = useState({ name: '', class: '', base_salary: '' });
  const [editingChar, setEditingChar] = useState(null);
  const [pilotMonthlyCap, setPilotMonthlyCap] = useState(0);
  const [pilotNickname, setPilotNickname] = useState('');
  const [isBasePayOnly, setIsBasePayOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) fetchPilots();
  }, [user]);

  const fetchPilots = async () => {
    const { data } = await supabase
      .from(TABLES.USERS)
      .select(`*, characters:${TABLES.CHARACTERS}(id, name, class, base_salary, pending_base_salary, salary_status)`)
      .eq('master_id', user.id);
    setPilots(data || []);
  };

  const notifyPilotOfChange = async (pilotId) => {
    await supabase
      .from(TABLES.USERS)
      .update({ profile_updated_at: new Date().toISOString() })
      .eq('id', pilotId);
  };

  const handleEditPilot = (pilot) => {
    setEditingPilot(pilot);
    setPilotMonthlyCap(pilot.monthly_cap || 0);
    setPilotNickname(pilot.nickname || '');
    setIsBasePayOnly(pilot.is_base_pay_only || false);
    fetchCharacters(pilot.id);
  };

  const fetchCharacters = async (pilotId) => {
    const { data } = await supabase.from(TABLES.CHARACTERS).select('*').eq('pilot_id', pilotId);
    setPilotCharacters(data || []);
  };

  const handleUpdatePilotSettings = async () => {
    setLoading(true);
    const { error } = await supabase
      .from(TABLES.USERS)
      .update({
        monthly_cap: parseFloat(pilotMonthlyCap),
        nickname: pilotNickname.trim(),
        is_base_pay_only: isBasePayOnly,
        profile_updated_at: new Date().toISOString()
      })
      .eq('id', editingPilot.id);
    
    if (!error) {
      alert('Pilot profile updated!');
      fetchPilots();
      setEditingPilot(null);
    } else {
      alert(error.message);
    }
    setLoading(false);
  };

  const addCharacter = async () => {
    if (!newChar.name || !newChar.class || !newChar.base_salary) return;
    setLoading(true);
    const { error } = await supabase.from(TABLES.CHARACTERS).insert([{
      pilot_id: editingPilot.id,
      name: newChar.name,
      class: newChar.class,
      base_salary: parseFloat(newChar.base_salary),
      salary_status: 'approved'
    }]);
    
    if (!error) {
      await notifyPilotOfChange(editingPilot.id);
      setNewChar({ name: '', class: '', base_salary: '' });
      fetchCharacters(editingPilot.id);
      fetchPilots();
    } else {
      alert(error.message);
    }
    setLoading(false);
  };

  const handleUpdateCharacter = async () => {
    if (!editingChar.name || !editingChar.class || editingChar.base_salary === '') return;
    setLoading(true);
    
    // Find original character to check if salary changed
    const original = pilotCharacters.find(c => c.id === editingChar.id);
    const salaryChanged = parseFloat(editingChar.base_salary) !== parseFloat(original.base_salary);
    
    const payload = {
      name: editingChar.name,
      class: editingChar.class,
    };

    // If salary changed, move it to pending and set status
    if (salaryChanged) {
      const remarks = prompt("Reason for salary change?");
      payload.pending_base_salary = parseFloat(editingChar.base_salary);
      payload.salary_status = 'pending_pilot';
      payload.master_remarks = remarks || 'Standard adjustment';
    } else {
      payload.base_salary = parseFloat(editingChar.base_salary);
    }

    const { error } = await supabase
      .from(TABLES.CHARACTERS)
      .update(payload)
      .eq('id', editingChar.id);

    if (!error) {
      await notifyPilotOfChange(editingPilot.id);
      alert(salaryChanged ? 'Salary update sent to pilot for approval!' : 'Character updated!');
      setEditingChar(null);
      fetchCharacters(editingPilot.id);
      fetchPilots();
    } else {
      alert(error.message);
    }
    setLoading(false);
  };

  const addPilot = async () => {
    if (!newPilot.username) return;
    setLoading(true);
    const { error } = await supabase.from(TABLES.USERS).insert([{
      username: newPilot.username.trim(),
      nickname: newPilot.nickname.trim(),
      role: 'pilot',
      master_id: user.id
    }]);
    if (!error) {
      alert('New Pilot account created!');
      setNewPilot({ username: '', nickname: '' });
      fetchPilots();
    } else {
      alert(error.message);
    }
    setLoading(false);
  };

  const totalBaseSalary = pilotCharacters.reduce((sum, char) => sum + (Number(char.base_salary) || 0), 0);

  return (
    <div className="space-y-12">
      <header>
        <h2 className="text-4xl font-black text-gray-900 tracking-tight">Pilot Roster</h2>
        <p className="text-gray-400 font-bold mt-2">Manage guild members and their character tiers.</p>
      </header>

      <div className="bento-card">
        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6 px-2">Register New Pilot</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input className="apple-input" placeholder="Unique Username" value={newPilot.username} onChange={e => setNewPilot({ ...newPilot, username: e.target.value })} />
          <input className="apple-input" placeholder="Display Nickname" value={newPilot.nickname} onChange={e => setNewPilot({ ...newPilot, nickname: e.target.value })} />
          <button onClick={addPilot} disabled={loading} className="apple-button-primary whitespace-nowrap">Create Account</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {pilots.map(p => {
          const pilotSalary = (p.characters || []).reduce((sum, char) => sum + (Number(char.base_salary) || 0), 0);
          return (
            <div key={p.id} className={`bento-card group hover:border-blue-200 transition-all border-2 ${p.is_base_pay_only ? 'border-amber-100 bg-amber-50/10' : 'border-transparent'}`}>
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-xl ${p.is_base_pay_only ? 'bg-amber-500' : 'bg-gray-900'}`}>
                    <SafeIcon icon={p.is_base_pay_only ? FiTarget : FiUser} className="text-xl" />
                  </div>
                  <div>
                    <h4 className="font-black text-lg text-gray-900">{p.nickname || p.username}</h4>
                    <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest">
                      {p.is_base_pay_only ? `Fixed: ₱${pilotSalary.toLocaleString()}` : `Cap: ₱${p.monthly_cap?.toLocaleString()}`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEditPilot(p)} className="p-2.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all"><FiEdit3 /></button>
                  <button onClick={() => { if (confirm('Delete pilot?')) supabase.from(TABLES.USERS).delete().eq('id', p.id).then(() => fetchPilots()); }} className="p-2.5 text-red-500 bg-red-50 hover:bg-red-100 rounded-xl transition-all"><FiTrash2 /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {editingPilot && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6 backdrop-blur-sm bg-black/30">
            <motion.div initial={{ opacity: 0, y: 100 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 100 }} className="relative w-full max-w-4xl bg-white rounded-[3rem] p-8 md:p-12 shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
              <div className="flex justify-between items-center mb-10 shrink-0">
                <div>
                  <h3 className="text-3xl font-black">Pilot Profile: {editingPilot.username}</h3>
                  <p className="text-gray-400 font-bold uppercase text-[10px] tracking-widest mt-1">Updates will notify the pilot</p>
                </div>
                <button onClick={() => setEditingPilot(null)} className="p-3 bg-gray-100 rounded-full text-gray-400 hover:bg-gray-200 transition-all"><FiX /></button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 overflow-y-auto pr-4 no-scrollbar pb-10">
                <div className="space-y-8">
                  <div className="bg-gray-50 p-8 rounded-[2.5rem] space-y-6">
                    <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-2">Nickname</label>
                      <input type="text" value={pilotNickname} onChange={(e) => setPilotNickname(e.target.value)} className="apple-input bg-white" placeholder="Display Name" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100">
                      <p className="text-xs font-black text-gray-900">Base Pay Only</p>
                      <button onClick={() => setIsBasePayOnly(!isBasePayOnly)} className={`w-12 h-6 rounded-full transition-all relative ${isBasePayOnly ? 'bg-amber-500' : 'bg-gray-200'}`} >
                        <motion.div animate={{ x: isBasePayOnly ? 26 : 2 }} className="absolute top-1 left-0 w-4 h-4 bg-white rounded-full shadow-sm" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-2">
                        {isBasePayOnly ? 'Calculated Monthly Base Salary' : 'Monthly Earnings Cap'}
                      </label>
                      <div className="relative">
                        <span className="absolute left-6 top-1/2 -translate-y-1/2 font-black text-gray-400 text-lg">₱</span>
                        <input type="number" disabled={isBasePayOnly} value={isBasePayOnly ? totalBaseSalary : pilotMonthlyCap} onChange={(e) => !isBasePayOnly && setPilotMonthlyCap(e.target.value)} className={`apple-input !pl-12 text-xl font-black bg-white ${isBasePayOnly ? 'text-amber-600 opacity-80' : 'text-blue-600'}`} />
                      </div>
                    </div>
                    <button onClick={handleUpdatePilotSettings} className="apple-button-primary w-full !py-4 text-xs">Save Settings</button>
                  </div>

                  <div className="bg-indigo-50/50 p-8 rounded-[2.5rem] border border-indigo-100/50 space-y-6">
                    <h4 className="text-[10px] font-black uppercase text-indigo-400 tracking-widest ml-2">{editingChar ? 'Edit Character' : 'Add New Character'}</h4>
                    <div className="space-y-4">
                      <input className="apple-input bg-white" placeholder="Character Name" value={editingChar ? editingChar.name : newChar.name} onChange={e => editingChar ? setEditingChar({ ...editingChar, name: e.target.value }) : setNewChar({ ...newChar, name: e.target.value })} />
                      <select className="apple-input bg-white" value={editingChar ? editingChar.class : newChar.class} onChange={e => editingChar ? setEditingChar({ ...editingChar, class: e.target.value }) : setNewChar({ ...newChar, class: e.target.value })}>
                        <option value="">Select Class</option>
                        {ROX_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input type="number" className="apple-input bg-white" placeholder="Base Salary (₱)" value={editingChar ? editingChar.base_salary : newChar.base_salary} onChange={e => editingChar ? setEditingChar({ ...editingChar, base_salary: e.target.value }) : setNewChar({ ...newChar, base_salary: e.target.value })} />
                      <div className="flex gap-2">
                        {editingChar && <button onClick={() => setEditingChar(null)} className="flex-1 apple-button-secondary !py-4 text-xs">Cancel</button>}
                        <button onClick={editingChar ? handleUpdateCharacter : addCharacter} className="flex-[2] apple-button-primary w-full !bg-indigo-600 !py-4 text-xs font-black">
                          {editingChar ? 'Update' : 'Register'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <h4 className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-4">Current Roster</h4>
                  {pilotCharacters.map(char => (
                    <div key={char.id} className={`p-6 bg-white border rounded-3xl shadow-sm flex items-center justify-between group transition-all ${char.salary_status === 'pending_pilot' ? 'border-amber-200 bg-amber-50/30' : 'border-gray-100 hover:border-blue-100'}`}>
                      <div className="flex items-center gap-6">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black shadow-lg ${char.salary_status === 'pending_pilot' ? 'bg-amber-500 text-white' : 'bg-indigo-600 text-white'}`}>
                          {char.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-black text-gray-900">{char.name}</div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black text-indigo-500 uppercase">{char.class}</span>
                            <span className="text-gray-300">&bull;</span>
                            <span className="text-[10px] font-black text-gray-900">₱{char.base_salary}</span>
                            {char.salary_status === 'pending_pilot' && (
                              <span className="flex items-center gap-1 text-[9px] font-black text-amber-600 uppercase">
                                <FiClock /> Pending ₱{char.pending_base_salary}
                              </span>
                            )}
                            {char.salary_status === 'contested' && (
                                <span className="flex items-center gap-1 text-[9px] font-black text-red-600 uppercase">
                                <FiAlertCircle /> Contested
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setEditingChar(char)} className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all"><FiEdit3 /></button>
                        <button onClick={() => { if (confirm('Remove character?')) supabase.from(TABLES.CHARACTERS).delete().eq('id', char.id).then(() => { fetchCharacters(editingPilot.id); fetchPilots(); notifyPilotOfChange(editingPilot.id); }); }} className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all"><FiTrash2 /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PilotManagement;